/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description guarantor utils
 */
/* eslint-disable no-self-assign */

import _ from 'lodash';
import { formatAmount } from './allAccounts';
import { getNameMeta } from 'utils/claims/institutionalClaimRequestParser';
import { contactPointSystems, contactPointUses, createContactPoint, getContactInfo } from 'utils/patients/patientInfo';

// get address meta
const getAddressMeta = (address: Fhir.Address): Guarantor.IAddressMeta => {
	return {
		street: address?.line?.[0],
		city: address?.city,
		state: address?.state,
		zip: address?.postalCode,
	};
};

// get initial model
const getInitialModel = (parsedGuarantor?: Guarantor.IParsedGuarantor): Record<keyof Guarantor.IFormModel, string> => {
	return {
		guarantorName: parsedGuarantor?.name || '',
		address: parsedGuarantor?.address?.street || '',
		city: parsedGuarantor?.address?.city || '',
		state: parsedGuarantor?.address?.state || undefined,
		zipCode: parsedGuarantor?.address?.zip || '',
		cellPhone: parsedGuarantor?.contactInfo?.cellPhone || '',
		workPhone: parsedGuarantor?.contactInfo?.workPhone || '',
		email: parsedGuarantor?.contactInfo?.email || '',
		preferredContactMethod: parsedGuarantor?.contactInfo?.preferredContact || undefined,
	};
};

// get parsed guarantor
const getParsedGuarantor = (guarantor: Guarantor.IResource): Guarantor.IParsedGuarantor => {
	const resourceType = guarantor?.resourceType;
	const name = _.isString(guarantor?.name) ? guarantor?.name : getNameMeta(guarantor?.name)?.[0]?.fullName;
	let address = {} as Guarantor.IAddressMeta;
	let contactInfo = {} as Patients.IContactInfo;
	if (resourceType === 'Organization') {
		address = getAddressMeta(guarantor?.contact?.[0]?.address);
		contactInfo = getContactInfo(guarantor?.contact?.[0]?.telecom);
	} else {
		address = getAddressMeta(guarantor?.address?.[0]);
		contactInfo = getContactInfo(guarantor?.telecom);
	}
	return {
		name,
		address,
		contactInfo,
	};
};

// prepare guarantor update model
const prepareGuarantorUpdateModel = (values: Guarantor.IFormModel, fhirGuarantor: Guarantor.IResource): Guarantor.IResource => {
	let model = _.cloneDeep(fhirGuarantor);
	const address = {
		line: _.isEmpty(values?.address) ? null : [values?.address],
		city: values?.city || '',
		state: values?.state || '',
		postalCode: values?.zipCode || '',
	};

	const telecom = _.compact([
		createContactPoint(contactPointSystems.phone, values?.cellPhone, contactPointUses.mobile),
		createContactPoint(contactPointSystems.phone, values?.workPhone, contactPointUses.work),
		createContactPoint(contactPointSystems.email, values?.email),
	]);
	// push the preferred method
	if (values?.preferredContactMethod) {
		telecom.push({
			system: values?.preferredContactMethod as Fhir.ContactPoint['system'],
			rank: 1,
		});
	}
	if (model?.resourceType === 'Organization') {
		model = model;
		model.contact = [
			{
				address,
				telecom,
			},
		];
	} else {
		model = model;
		model.address = [address];
		model.telecom = telecom;
	}

	return model;
};

// guarantor source
const guarantorSource: Record<string, Guarantor.GuarantorSource> = {
	veritable: 'veritable',
	ehr: 'ehr',
};

const getOutstandingPR = ({
	patientResponsibility,
	allocated,
	writeOffAmount,
}: {
	patientResponsibility?: number;
	allocated?: number;
	writeOffAmount?: number;
}): string | null => {
	if (!_.isNumber(patientResponsibility)) {
		return null;
	}
	return formatAmount(patientResponsibility - (allocated || 0) - (writeOffAmount || 0));
};

export { getInitialModel, getParsedGuarantor, prepareGuarantorUpdateModel, guarantorSource, getOutstandingPR };
